/**
 * WebSocket hook for real-time chat functionality.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuthStore } from "../stores/authStore";

interface WebSocketMessage {
  type: 'new_message' | 'typing' | 'read_receipt' | 'user_status';
  room_id?: number;
  message?: any;
  user_id?: number;
  is_typing?: boolean;
  message_id?: number;
  status?: string;
  timestamp: string;
}

interface UseChatWebSocketOptions {
  roomId: number;
  onNewMessage?: (message: any) => void;
  onTyping?: (userId: number, isTyping: boolean) => void;
  onReadReceipt?: (messageId: number, userId: number) => void;
  onUserStatus?: (userId: number, status: string) => void;
}

export const useChatWebSocket = ({
  roomId,
  onNewMessage,
  onTyping,
  onReadReceipt,
  onUserStatus,
}: UseChatWebSocketOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { token } = useAuthStore();
  // Keep stable refs for handlers to avoid reconnect loops
  const onNewMessageRef = useRef<typeof onNewMessage>(onNewMessage);
  const onTypingRef = useRef<typeof onTyping>(onTyping);
  const onReadReceiptRef = useRef<typeof onReadReceipt>(onReadReceipt);
  const onUserStatusRef = useRef<typeof onUserStatus>(onUserStatus);

  useEffect(() => { onNewMessageRef.current = onNewMessage; }, [onNewMessage]);
  useEffect(() => { onTypingRef.current = onTyping; }, [onTyping]);
  useEffect(() => { onReadReceiptRef.current = onReadReceipt; }, [onReadReceipt]);
  useEffect(() => { onUserStatusRef.current = onUserStatus; }, [onUserStatus]);

  const connect = useCallback(() => {
    if (!token || !roomId) return;

    try {
      // Déterminer le protocole WebSocket (ws ou wss)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Utiliser le même host que la page pour que le proxy Vite fonctionne
      const host = window.location.host;
      
      // Construire l'URL WebSocket
      const wsUrl = `${protocol}//${host}/api/v1/chat/ws/${roomId}?token=${token}`;
      
      console.log('[WebSocket] Connecting to:', wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`WebSocket connected to room ${roomId}`);
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);

          switch (data.type) {
            case 'new_message':
              if (data.message && onNewMessageRef.current) {
                onNewMessageRef.current(data.message);
              }
              break;

            case 'typing':
              if (data.user_id !== undefined && data.is_typing !== undefined && onTypingRef.current) {
                onTypingRef.current(data.user_id, data.is_typing);
              }
              break;

            case 'read_receipt':
              if (data.message_id && data.user_id && onReadReceiptRef.current) {
                onReadReceiptRef.current(data.message_id, data.user_id);
              }
              break;

            case 'user_status':
              if (data.user_id && data.status && onUserStatusRef.current) {
                onUserStatusRef.current(data.user_id, data.status);
              }
              break;

            default:
              console.warn('Unknown WebSocket message type:', data.type);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('Erreur de connexion WebSocket');
      };

      ws.onclose = () => {
        console.log(`WebSocket disconnected from room ${roomId}`);
        setIsConnected(false);

        // Tentative de reconnexion après 3 secondes
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 3000);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Error creating WebSocket:', err);
      setError('Impossible de créer la connexion WebSocket');
    }
  }, [roomId, token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  const sendTyping = useCallback((isTyping: boolean) => {
    console.log('sendTyping called:', { isTyping, wsState: wsRef.current?.readyState });
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: 'typing',
        is_typing: isTyping,
      };
      console.log('Sending typing message:', message);
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('Cannot send typing - WebSocket not ready');
    }
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'message',
          message,
        })
      );
    }
  }, []);

  // Connexion au montage
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    error,
    sendTyping,
    sendMessage,
    reconnect: connect,
  };
};
